import { useClerk, useUser } from '@clerk/clerk-react';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { BackendTypes } from '@tf/api';
import {
	ActionIcon,
	AppShell,
	Box,
	Center,
	createStyles,
	Flex,
	Icon,
	Modal,
	ModalFooter,
	TFText,
	Tooltip,
	UnstyledButton,
	useDisclosure,
	useMantineTheme,
} from '@tf/ui';
import type React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CompanyLogo } from '@/components/shared';
import { useFormControlRefs } from '@/core/providers';

import { UserLocaleSetter } from './LocaleSwitcher';
import { useMenu } from './MenuProvider';

const useStyles = createStyles(({ colors }) => {
	return {
		root: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'space-between',
			alignItems: 'stretch',
			padding: '1rem',
		},
		logoContainer: {
			display: 'flex',
			justifyContent: 'center',
		},
		menuContainer: {
			flex: 1,
			overflowY: 'auto',
		},
		userEmail: {
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
			overflowX: 'hidden',
			fontSize: '14px',
			color: colors.gray[9],
		},
		verticalLine: {
			position: 'absolute',
			top: 0,
			left: '50%',
			width: '2px',
			height: '50%',
			transform: 'translateX(-50%)',
		},
		iconContainer: {
			width: '24px',
			height: '24px',
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)',
			zIndex: 1,
		},
	};
});

export const Sidebar: React.FC = () => {
	const { classes } = useStyles();
	const { _ } = useLingui();
	const navigate = useNavigate();
	const { menuItems } = useMenu();
	const { signOut } = useClerk();
	const { user } = useUser();
	const logoutModal = useDisclosure();
	const { formControlRef, submitTypeRef } = useFormControlRefs();
	const { colors, radius } = useMantineTheme();

	return (
		<AppShell.Navbar className={classes.root} w={{ base: 300 }} p="1rem">
			<Box className={classes.logoContainer}>
				<Link to="/" style={{ textDecoration: 'none' }}>
					<CompanyLogo />
				</Link>
			</Box>

			{menuItems.length > 0 && (
				<UnstyledButton
					sx={({ colors }) => ({
						color: colors.brand[6],
						'&:hover': {
							color: colors.brand[8],
							fill: colors.brand[8],
						},
					})}
					m="16px 0"
					onClick={() => navigate('/')}
				>
					<Flex align="center">
						<Icon.IconChevronLeft size={18} />
						<TFText fz="14px" fw="500">
							<Trans id="sidebar.goBackButton">Exit application</Trans>
						</TFText>
					</Flex>
				</UnstyledButton>
			)}

			<Box className={classes.menuContainer}>
				{menuItems.map((menuItem, index) => {
					return (
						<MenuItem
							key={menuItem.label}
							index={index}
							onClick={() => {
								menuItem.onClick();
								window.scrollTo(0, 0);
							}}
							label={menuItem.label}
							status={menuItem.status}
						/>
					);
				})}
			</Box>

			<Flex align="center" gap="8px">
				<Center
					sx={{
						background: colors.gray[1],
						width: '32px',
						height: '32px',
						borderRadius: radius.round_999,
						color: colors.gray[5],
					}}
				>
					<Icon.IconUserFilled color="red" size={18} />
				</Center>

				<Tooltip label={user?.primaryEmailAddress?.emailAddress}>
					<TFText maw="180px" className={classes.userEmail}>
						{user?.primaryEmailAddress?.emailAddress}
					</TFText>
				</Tooltip>

				{/* NOTE: decided we don't need switcher now */}
				{/*<LocaleSwitcher />*/}
				{/* So, we just save current locale chosen by our app in user preferences */}
				<UserLocaleSetter />

				<ActionIcon ml="8px" variant="transparent" name="save" color={colors.gray[5]} onClick={logoutModal.open}>
					<Icon.IconLogout size={24} />
				</ActionIcon>
			</Flex>
			<Modal
				size="500px"
				opened={logoutModal.isOpen}
				onClose={logoutModal.close}
				title={_(msg({ id: 'sidebar.logoutModal.title', message: 'Log out?' }))}
			>
				<Box px="1rem" py="0.75rem">
					<TFText c={colors.gray[9]} fz="14px" lh="18px">
						<Trans id="sidebar.logoutModal.text">
							Although we save most data automatically, we still recommend that you save your application before you
							leave
						</Trans>
					</TFText>
				</Box>
				<ModalFooter
					onCancel={logoutModal.close}
					cancelLabel={_(msg({ id: 'sidebar.logoutModal.cancelButton', message: 'Cancel' }))}
					submitLabel={_(msg({ id: 'sidebar.logoutModal.submitButton', message: 'Log out' }))}
					onSubmit={async () => {
						if (formControlRef && submitTypeRef) {
							submitTypeRef.current = 'quietSave';
							await formControlRef?.current?.submit();
						}
						await signOut();
						navigate('/');
					}}
				/>
			</Modal>
		</AppShell.Navbar>
	);
};

interface MenuItemProps {
	onClick: () => void;
	label: string;
	index: number;
	status: BackendTypes.FieldValidationStatus;
}

const MenuItem: React.FC<MenuItemProps> = ({ onClick, label, index, status }) => {
	const { colors } = useMantineTheme();

	const { menuItems } = useMenu();
	const selectedIndex = menuItems.findIndex((item) => item.isSelected);
	const isActive = selectedIndex === index;

	return (
		<UnstyledButton
			sx={({ colors, radius }) => ({
				display: 'flex',
				height: '48px',
				alignItems: 'center',
				width: '100%',
				borderRadius: radius.round_8,
				backgroundColor: isActive ? colors.slate[1] : undefined,
				'&:hover': {
					backgroundColor: !isActive ? colors.slate[0] : undefined,
				},
			})}
			onClick={onClick}
		>
			<Box ml="8px">
				<ProgressIcon index={index} status={status} />
			</Box>
			<TFText lineClamp={2} m="15px 8px 15px 12px" c={colors.gray[9]} lh="18px" fw="500" fz="14px">
				{label}
			</TFText>
		</UnstyledButton>
	);
};

interface ProgressIconProps {
	index: number;
	status: BackendTypes.FieldValidationStatus;
}

const ProgressIcon: React.FC<ProgressIconProps> = ({ index, status }) => {
	const { colors } = useMantineTheme();
	const { classes } = useStyles();

	const { menuItems } = useMenu();
	const selectedIndex = menuItems.findIndex((item) => item.isSelected);

	const isActive = selectedIndex === index;
	const hasError = status === 'VALIDATION_ERRORS';
	const isFilled = status !== 'PENDING_FILL';
	const isFirst = index === 0;
	const isLast = index === menuItems.length - 1;
	const firstUnfilledIndex = menuItems.findIndex((item) => item.status === 'PENDING_FILL');

	const iconStyles = {
		activeError: (
			<g>
				<circle cx="12" cy="12" r="10.5" stroke={colors.red[2]} strokeWidth="3" />
				<circle cx="12" cy="12" r="9.5" fill="white" stroke={colors.red[7]} />
				<circle cx="12" cy="12" r="4" fill={colors.red[7]} />
			</g>
		),
		error: (
			<g>
				<circle cx="12" cy="12" r="9.5" fill="white" stroke={colors.red[7]} />
				<circle cx="12" cy="12" r="4" fill={colors.red[7]} />
			</g>
		),
		active: (
			<g>
				<circle cx="12" cy="12" r="10.5" stroke={colors.brand[2]} strokeWidth="3" />
				<circle cx="12" cy="12" r="9.5" fill="white" stroke={colors.brand[6]} />
				<circle cx="12" cy="12" r="4" fill={colors.brand[6]} />
			</g>
		),
		filled: (
			<g>
				<circle cx="12" cy="12" r="10" fill={colors.brand[6]} />
				<circle cx="12" cy="12" r="4" fill={colors.gray[0]} />
			</g>
		),
		default: (
			<g>
				<circle cx="12" cy="12" r="9.5" fill="white" stroke={colors.gray[3]} />
				<circle cx="12" cy="12" r="4" fill={colors.gray[3]} />
			</g>
		),
	};

	const getIcon = () => {
		if (hasError && isActive) return iconStyles.activeError;
		if (hasError) return iconStyles.error;
		if (isActive && isFilled) return iconStyles.filled;
		if (isActive) return iconStyles.active;
		if (isFilled) return iconStyles.filled;
		return iconStyles.default;
	};

	const getVerticalLineColor = (position: 'top' | 'bottom') => {
		const isOneBefore = index === firstUnfilledIndex - 1;
		const isFirstUnfilledStep = index === firstUnfilledIndex;
		const isSelected = index === selectedIndex;

		if (isOneBefore && position === 'bottom' && index + 1 !== selectedIndex) return colors.gray[2];
		if (isFirstUnfilledStep && isSelected && position === 'top') return colors.brand[6];
		if (isOneBefore && position === 'bottom') return colors.brand[6];
		if (index < firstUnfilledIndex) return colors.brand[6];

		return colors.gray[2];
	};

	const topVerticalLineColor = getVerticalLineColor('top');
	const bottomVerticalLineColor = getVerticalLineColor('bottom');

	return (
		<Box sx={{ position: 'relative', width: '24px', height: '48px' }}>
			{!isFirst && <Box className={classes.verticalLine} bg={topVerticalLineColor} />}
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={classes.iconContainer}>
				{getIcon()}
			</svg>
			{!isLast && <Box className={classes.verticalLine} bg={bottomVerticalLineColor} sx={{ top: '50%' }} />}
		</Box>
	);
};
